/**
 * UsersSection - Settings panel for admin user management.
 *
 * Reads and mutates `/api/admin/users*` via the shared `adminFetch` helper.
 * All actions require the matching `u.*` permission on the current user.
 */
import {
  type AllowedModel,
  GLOBAL_PERMISSIONS,
  type GlobalPermission,
  type ManagedAgentId,
  MODEL_OPTIONS_BY_PROVIDER,
  type ProjectId,
  type ProjectPermission,
  PROJECT_PERMISSIONS,
  PROVIDER_DISPLAY_NAMES,
  type ProviderKind,
  USER_EMAIL_MAX_LENGTH,
  USER_NAME_MAX_LENGTH,
} from "@taskin-code/contracts";
import { canRunAsPrimary } from "@taskin-code/shared/managedAgentMode";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";

import { managedAgentsQueryOptions } from "../../agents/managedAgentsReactQuery";
import { useAuthStore } from "../../auth/authStore";
import { adminFetch as fetchJson } from "../../lib/adminFetch";
import { readFileAsDataUrl } from "../../lib/readFileAsDataUrl";
import { userDirectoryQueryKey } from "../../lib/userDirectoryReactQuery";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useStore } from "../../store";
import { UserAvatar } from "../UserAvatar";
import { AVATAR_FILE_INPUT_ACCEPT, validateAvatarFile } from "./ProfileSection.logic";
import { Field, ModalShell } from "./settingsModal";

interface AdminUserRow {
  readonly userId: string;
  readonly email: string;
  readonly name: string;
  readonly roleLabel: string | null;
  readonly mustChangePassword: boolean;
  readonly createdAt: string;
  readonly globalPermissions: ReadonlyArray<GlobalPermission>;
  readonly projectAssignments: ReadonlyArray<{
    readonly projectId: ProjectId;
    readonly permissions: ReadonlyArray<ProjectPermission>;
  }>;
  readonly allowedModels: ReadonlyArray<AllowedModel>;
  readonly defaultModel: AllowedModel | null;
  readonly allowedAgentIds: ReadonlyArray<ManagedAgentId>;
  readonly defaultAgentId: ManagedAgentId | null;
  readonly totpActivated: boolean;
  readonly avatarFileName: string | null;
}

export function UsersSection() {
  const currentUser = useAuthStore((state) => state.user);
  const isAdmin = useAuthStore((state) => state.globalPermissions.has("u.view"));
  const projects = useStore((state) => state.projects);
  const [users, setUsers] = useState<ReadonlyArray<AdminUserRow>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);

  const refreshUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchJson<{ users: ReadonlyArray<AdminUserRow> }>("/api/admin/users");
      setUsers(result.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    void refreshUsers();
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="rounded-md border border-border/60 bg-card/40 p-6 text-sm text-muted-foreground">
        You need the <code>u.view</code> permission to manage users.
      </div>
    );
  }

  const editingUser = users.find((u) => u.userId === editingUserId) ?? null;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold">Users</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Create users, grant global permissions, and assign projects.
          </p>
        </div>
        <Button size="sm" className="shrink-0" onClick={() => setCreateOpen(true)}>
          + New user
        </Button>
      </div>

      {error ? (
        <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
          {error}
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-border/60 bg-card/40">
        <table className="w-full min-w-[880px] text-sm">
          <thead className="border-b border-border/40 bg-muted/30 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="w-[26%] px-4 py-2 font-medium">User</th>
              <th className="w-[14%] px-4 py-2 font-medium">Role</th>
              <th className="px-4 py-2 font-medium">Global perms</th>
              <th className="px-4 py-2 font-medium">Projects</th>
              <th className="w-[1%] px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && users.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-xs text-muted-foreground">
                  Loading users…
                </td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-xs text-muted-foreground">
                  No users found.
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <tr
                  key={user.userId}
                  className="border-b border-border/30 align-top last:border-b-0"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <UserAvatar
                        userId={user.userId}
                        displayName={user.name}
                        avatarFileName={user.avatarFileName}
                        size="md"
                        className="shrink-0"
                      />
                      <div className="flex flex-col">
                        <span className="font-medium">{user.email}</span>
                        <span className="text-xs text-muted-foreground">{user.name}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">{user.roleLabel ?? "—"}</td>
                  <td className="px-4 py-3">
                    {user.globalPermissions.length === 0 ? (
                      <span className="text-xs text-muted-foreground">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {user.globalPermissions.map((perm) => (
                          <span
                            key={perm}
                            className="rounded border border-emerald-500/40 bg-emerald-500/10 px-1.5 py-0.5 font-mono text-[10px] text-emerald-200"
                          >
                            {perm}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">
                    {user.projectAssignments.length === 0
                      ? "—"
                      : `${user.projectAssignments.length} project${user.projectAssignments.length === 1 ? "" : "s"}`}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingUserId(user.userId)}
                    >
                      Manage
                    </Button>
                    {user.totpActivated ? (
                      // The only way back for a user who lost both their phone
                      // and their recovery codes — self-service disable needs a
                      // session they cannot obtain.
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-2"
                        onClick={async () => {
                          if (
                            !window.confirm(
                              `Reset two-factor authentication for ${user.email}? ` +
                                `They will sign in with their password alone until they set it up again.`,
                            )
                          ) {
                            return;
                          }
                          try {
                            await fetchJson(`/api/admin/users/${user.userId}/totp`, {
                              method: "DELETE",
                            });
                            void refreshUsers();
                          } catch (err) {
                            setError(err instanceof Error ? err.message : "Reset failed");
                          }
                        }}
                      >
                        Reset 2FA
                      </Button>
                    ) : null}
                    {currentUser?.id === user.userId ? null : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="ml-2 text-red-500 hover:text-red-400"
                        onClick={async () => {
                          if (
                            !window.confirm(`Soft-delete ${user.email}? Their sessions will end.`)
                          ) {
                            return;
                          }
                          try {
                            await fetchJson(`/api/admin/users/${user.userId}`, {
                              method: "DELETE",
                            });
                            void refreshUsers();
                          } catch (err) {
                            setError(err instanceof Error ? err.message : "Delete failed");
                          }
                        }}
                      >
                        Delete
                      </Button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {createOpen ? (
        <CreateUserDialog
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            void refreshUsers();
          }}
        />
      ) : null}

      {editingUser ? (
        <ManageUserDialog
          user={editingUser}
          projects={projects.map((p) => ({ id: p.id, title: p.name }))}
          onClose={() => setEditingUserId(null)}
          onChanged={() => void refreshUsers()}
        />
      ) : null}
    </div>
  );
}

function CreateUserDialog(props: { onClose: () => void; onCreated: () => void }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [roleLabel, setRoleLabel] = useState("");
  const [password, setPassword] = useState("password");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await fetchJson("/api/admin/users", {
        method: "POST",
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim(),
          roleLabel: roleLabel.trim() || null,
          password,
          mustChangePassword: true,
        }),
      });
      props.onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create user");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalShell title="Create user" onClose={props.onClose}>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <Field label="Email">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required />
        </Field>
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} required />
        </Field>
        <Field label="Role label (optional)">
          <Input
            value={roleLabel}
            onChange={(e) => setRoleLabel(e.target.value)}
            placeholder="e.g. Developer, PM, QA"
          />
        </Field>
        <Field label="Initial password">
          <Input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            type="text"
            minLength={8}
            required
          />
        </Field>
        {error ? (
          <div className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">
            {error}
          </div>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={props.onClose}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={submitting}>
            {submitting ? "Creating…" : "Create"}
          </Button>
        </div>
      </form>
    </ModalShell>
  );
}

function ManageUserDialog(props: {
  user: AdminUserRow;
  projects: ReadonlyArray<{ id: ProjectId; title: string }>;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { user } = props;
  const [error, setError] = useState<string | null>(null);
  const currentUser = useAuthStore((state) => state.user);
  const applyUser = useAuthStore((state) => state.applyUser);
  const queryClient = useQueryClient();
  const avatarFileInputRef = useRef<HTMLInputElement>(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [nameDraft, setNameDraft] = useState(user.name);
  const [emailDraft, setEmailDraft] = useState(user.email);
  const [identityBusy, setIdentityBusy] = useState(false);

  // Re-seeded from the refreshed row after a save, so the fields show what was
  // actually stored (the server trims the name and lowercases the address)
  // rather than what was typed.
  useEffect(() => {
    setNameDraft(user.name);
  }, [user.name]);
  useEffect(() => {
    setEmailDraft(user.email);
  }, [user.email]);

  const trimmedName = nameDraft.trim();
  const normalizedEmail = emailDraft.trim().toLowerCase();
  const identityChanged =
    trimmedName.length > 0 &&
    normalizedEmail.length > 0 &&
    (trimmedName !== user.name || normalizedEmail !== user.email);

  /**
   * Name and email in one PATCH.
   *
   * Email is the login identifier, which is why this lives on the admin screen
   * and not in the user's own Profile section. A 409 comes back when another
   * live account already holds the address; the message is shown as-is.
   */
  const saveIdentity = (event: React.FormEvent) => {
    event.preventDefault();
    if (!identityChanged || identityBusy) return;
    setIdentityBusy(true);
    setError(null);
    void (async () => {
      try {
        await fetchJson(`/api/admin/users/${user.userId}`, {
          method: "PATCH",
          body: JSON.stringify({ name: trimmedName, email: normalizedEmail }),
        });
        if (currentUser?.id === user.userId) {
          applyUser({ ...currentUser, name: trimmedName, email: normalizedEmail });
        }
        void queryClient.invalidateQueries({ queryKey: userDirectoryQueryKey });
        props.onChanged();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to save the account details");
      } finally {
        setIdentityBusy(false);
      }
    })();
  };

  /**
   * Same repaint as `ProfileSection.settle`, but for someone else's account: the
   * table's row comes from `onChanged` re-fetching the list, the directory
   * query is invalidated for every other surface, and the auth store is only
   * touched when the admin happened to edit their own row — otherwise there is
   * no session of theirs to update.
   */
  const settleAvatar = (avatarFileName: string | null) => {
    if (currentUser?.id === user.userId) {
      applyUser({ ...currentUser, avatarFileName });
    }
    void queryClient.invalidateQueries({ queryKey: userDirectoryQueryKey });
    props.onChanged();
  };

  const runAvatar = async (action: () => Promise<{ avatarFileName: string | null }>) => {
    if (avatarBusy) return;
    setAvatarBusy(true);
    setError(null);
    try {
      settleAvatar((await action()).avatarFileName);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update photo");
    } finally {
      setAvatarBusy(false);
    }
  };

  const onAvatarFilePicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared immediately so picking the same file twice still fires a change
    // event — a failed upload could otherwise not be retried with the same
    // photo without choosing a different one first.
    event.target.value = "";
    if (!file) return;

    const verdict = validateAvatarFile(file);
    if (!verdict.ok) {
      setError(verdict.message);
      return;
    }

    void runAvatar(async () => {
      const dataUrl = await readFileAsDataUrl(file);
      return fetchJson<{ avatarFileName: string | null }>(
        `/api/admin/users/${user.userId}/avatar`,
        { method: "PUT", body: JSON.stringify({ dataUrl }) },
      );
    });
  };

  const removeAvatar = () =>
    void runAvatar(() =>
      fetchJson<{ avatarFileName: string | null }>(`/api/admin/users/${user.userId}/avatar`, {
        method: "DELETE",
      }),
    );

  const togglePermission = async (perm: GlobalPermission, currentlyHas: boolean) => {
    setError(null);
    try {
      await fetchJson(`/api/admin/users/${user.userId}/permissions/${perm}`, {
        method: currentlyHas ? "DELETE" : "PUT",
      });
      props.onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to toggle permission");
    }
  };

  const setProjectPermissions = async (
    projectId: ProjectId,
    permissions: ReadonlyArray<ProjectPermission>,
  ) => {
    setError(null);
    try {
      if (permissions.length === 0) {
        await fetchJson(`/api/admin/users/${user.userId}/projects/${projectId}`, {
          method: "DELETE",
        });
      } else {
        await fetchJson(`/api/admin/users/${user.userId}/projects/${projectId}`, {
          method: "PUT",
          body: JSON.stringify({ permissions }),
        });
      }
      props.onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update assignment");
    }
  };

  const projectPermsByProject = useMemo(() => {
    const map = new Map<ProjectId, ReadonlySet<ProjectPermission>>();
    for (const assignment of user.projectAssignments) {
      map.set(assignment.projectId, new Set(assignment.permissions));
    }
    return map;
  }, [user.projectAssignments]);

  const modelKey = (provider: ProviderKind, slug: string) => `${provider}:${slug}`;
  const allowedModelKeys = useMemo(() => {
    const set = new Set<string>();
    for (const m of user.allowedModels) set.add(modelKey(m.provider, m.model));
    return set;
  }, [user.allowedModels]);

  const toggleAllowedModel = async (
    provider: ProviderKind,
    slug: string,
    currentlyAllowed: boolean,
  ) => {
    setError(null);
    try {
      await fetchJson(
        `/api/admin/users/${user.userId}/models/${provider}/${encodeURIComponent(slug)}`,
        { method: currentlyAllowed ? "DELETE" : "PUT" },
      );
      props.onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to toggle model");
    }
  };

  const setDefaultModel = async (selection: AllowedModel | null) => {
    setError(null);
    try {
      await fetchJson(`/api/admin/users/${user.userId}/default-model`, {
        method: "PUT",
        body: JSON.stringify(
          selection
            ? { provider: selection.provider, model: selection.model }
            : { provider: null, model: null },
        ),
      });
      props.onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update default model");
    }
  };

  const allowedAgentIds = useMemo(
    () => new Set<string>(user.allowedAgentIds),
    [user.allowedAgentIds],
  );

  // What this admin can see, which is every agent for a holder of `a.view` and
  // only their own teams' otherwise — the same caveat the Agents screen carries.
  //
  // Narrowed to the agents a person can be given: both settings on this screen
  // pre-select an agent on someone else's behalf, and a subagent-only agent would
  // be refused by the server on save — and, if it somehow got stored, would leave
  // the account with an exclusive allowlist it can never dispatch anything from.
  const agentCatalogQuery = useQuery(managedAgentsQueryOptions());
  const agentCatalog = useMemo(
    () => (agentCatalogQuery.data ?? []).filter((agent) => canRunAsPrimary(agent.mode)),
    [agentCatalogQuery.data],
  );

  /**
   * The default has to name something the user can actually reach, and which
   * agents those are depends on whether they have a personal allowlist at all.
   * With grants, the choices are the grants; without, every agent stays eligible
   * because team scoping — not this screen — decides reachability. Offering the
   * full catalog in the first case would let an admin pick a default the server
   * then refuses.
   */
  const defaultAgentChoices = useMemo(
    () =>
      allowedAgentIds.size === 0
        ? agentCatalog
        : agentCatalog.filter((agent) => allowedAgentIds.has(agent.id)),
    [agentCatalog, allowedAgentIds],
  );

  const toggleAllowedAgent = async (agentId: ManagedAgentId, currentlyAllowed: boolean) => {
    setError(null);
    try {
      await fetchJson(`/api/admin/users/${user.userId}/agents/${encodeURIComponent(agentId)}`, {
        method: currentlyAllowed ? "DELETE" : "PUT",
      });
      props.onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to toggle agent");
    }
  };

  const setDefaultAgent = async (agentId: ManagedAgentId | null) => {
    setError(null);
    try {
      await fetchJson(`/api/admin/users/${user.userId}/default-agent`, {
        method: "PUT",
        body: JSON.stringify({ agentId }),
      });
      props.onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update default agent");
    }
  };

  return (
    <ModalShell title={`Manage ${user.email}`} onClose={props.onClose} wide>
      <div className="flex flex-col gap-5">
        {/*
          Pinned to the top of the scrollport rather than sitting at the end of
          the dialog: this panel is taller than the viewport, and an error
          rendered below the last section is an error nobody sees — a rejected
          save (a duplicate email answers 409) looked like nothing happened.
        */}
        {error ? (
          <div className="sticky top-0 z-10 rounded border border-red-500/40 bg-red-500/15 px-3 py-2 text-xs text-red-200 backdrop-blur">
            {error}
          </div>
        ) : null}

        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Account
          </h4>
          <form className="mt-2 flex flex-col gap-3" onSubmit={saveIdentity}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Name">
                <Input
                  value={nameDraft}
                  maxLength={USER_NAME_MAX_LENGTH}
                  disabled={identityBusy}
                  onChange={(e) => setNameDraft(e.target.value)}
                />
              </Field>
              <Field label="Email">
                <Input
                  value={emailDraft}
                  type="email"
                  maxLength={USER_EMAIL_MAX_LENGTH}
                  disabled={identityBusy}
                  onChange={(e) => setEmailDraft(e.target.value)}
                />
              </Field>
            </div>
            <div className="flex items-center gap-3">
              <Button type="submit" size="sm" disabled={identityBusy || !identityChanged}>
                {identityBusy ? "Saving…" : "Save"}
              </Button>
              <span className="text-[11px] text-muted-foreground">
                The email is what this user signs in with. Only administrators can change it.
              </span>
            </div>
          </form>
        </section>

        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Photo
          </h4>
          <div className="mt-2 flex items-center gap-3">
            <UserAvatar
              userId={user.userId}
              displayName={user.name}
              avatarFileName={user.avatarFileName}
              size="lg"
            />
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={avatarFileInputRef}
                type="file"
                accept={AVATAR_FILE_INPUT_ACCEPT}
                className="hidden"
                onChange={onAvatarFilePicked}
              />
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={avatarBusy}
                onClick={() => avatarFileInputRef.current?.click()}
              >
                {avatarBusy ? "Working…" : user.avatarFileName ? "Change photo" : "Upload photo"}
              </Button>
              {user.avatarFileName ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={avatarBusy}
                  onClick={removeAvatar}
                >
                  Remove photo
                </Button>
              ) : null}
            </div>
          </div>
        </section>

        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Global permissions
          </h4>
          <div className="mt-2 flex flex-wrap gap-2">
            {GLOBAL_PERMISSIONS.map((perm) => {
              const has = user.globalPermissions.includes(perm);
              return (
                <button
                  key={perm}
                  type="button"
                  onClick={() => void togglePermission(perm, has)}
                  className={`rounded border px-2 py-1 font-mono text-[11px] transition-colors ${
                    has
                      ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-200"
                      : "border-border/50 bg-muted/30 text-muted-foreground hover:border-border"
                  }`}
                >
                  {perm}
                </button>
              );
            })}
          </div>
        </section>

        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Projects
          </h4>
          <div className="mt-2 flex flex-col gap-2">
            {props.projects.length === 0 ? (
              <p className="text-xs text-muted-foreground">No projects to assign yet.</p>
            ) : (
              props.projects.map((project) => {
                const granted = projectPermsByProject.get(project.id) ?? new Set();
                const isAssigned = projectPermsByProject.has(project.id);
                return (
                  <div
                    key={project.id}
                    className="rounded-md border border-border/40 bg-card/30 p-2"
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{project.title}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          void setProjectPermissions(
                            project.id,
                            isAssigned
                              ? []
                              : (PROJECT_PERMISSIONS as unknown as ReadonlyArray<ProjectPermission>),
                          )
                        }
                      >
                        {isAssigned ? "Unassign" : "Assign with all perms"}
                      </Button>
                    </div>
                    {isAssigned ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {PROJECT_PERMISSIONS.map((perm) => {
                          const has = granted.has(perm);
                          return (
                            <button
                              key={perm}
                              type="button"
                              onClick={() => {
                                const next = new Set(granted);
                                if (has) next.delete(perm);
                                else next.add(perm);
                                void setProjectPermissions(project.id, Array.from(next));
                              }}
                              className={`rounded border px-2 py-0.5 font-mono text-[11px] ${
                                has
                                  ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-200"
                                  : "border-border/50 bg-muted/30 text-muted-foreground"
                              }`}
                            >
                              {perm}
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </section>

        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Allowed models
          </h4>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Toggle which (provider, model) pairs the user is permitted to dispatch. Backend rejects
            forbidden models on thread.create / thread.turn.start.
          </p>
          <div className="mt-3 flex flex-col gap-3">
            {(Object.keys(MODEL_OPTIONS_BY_PROVIDER) as ProviderKind[]).map((provider) => (
              <div key={provider}>
                <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
                  {PROVIDER_DISPLAY_NAMES[provider]}
                </div>
                <div className="mt-1 flex flex-wrap gap-2">
                  {MODEL_OPTIONS_BY_PROVIDER[provider].map((model) => {
                    const has = allowedModelKeys.has(modelKey(provider, model.slug));
                    return (
                      <button
                        key={model.slug}
                        type="button"
                        onClick={() => void toggleAllowedModel(provider, model.slug, has)}
                        className={`rounded border px-2 py-1 font-mono text-[11px] transition-colors ${
                          has
                            ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-200"
                            : "border-border/50 bg-muted/30 text-muted-foreground hover:border-border"
                        }`}
                      >
                        {model.slug}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Default model
          </h4>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Applied when the user creates a thread and the project has no default. Must be in the
            allowlist.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <select
              value={
                user.defaultModel
                  ? modelKey(user.defaultModel.provider, user.defaultModel.model)
                  : ""
              }
              onChange={(event) => {
                const value = event.target.value;
                if (!value) {
                  void setDefaultModel(null);
                  return;
                }
                const [provider, ...slugParts] = value.split(":");
                const slug = slugParts.join(":");
                if (provider !== "codex" && provider !== "claudeAgent") return;
                void setDefaultModel({ provider, model: slug });
              }}
              className="rounded border border-border/60 bg-background px-2 py-1 text-xs"
            >
              <option value="">None (use project / system default)</option>
              {user.allowedModels.map((m) => (
                <option key={modelKey(m.provider, m.model)} value={modelKey(m.provider, m.model)}>
                  {PROVIDER_DISPLAY_NAMES[m.provider]} — {m.model}
                </option>
              ))}
            </select>
          </div>
        </section>

        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Allowed agents
          </h4>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Leave every agent off to keep the default behaviour: the user sees the agents granted to
            their teams, plus any agent granted to no team. Turning even one on makes this list
            exclusive — they will see only what is selected here, and the backend rejects anything
            else on thread.create / thread.turn.start.
          </p>
          {agentCatalog.length === 0 ? (
            <p className="mt-2 text-[11px] text-muted-foreground/70">
              No agents available to assign. Note that this list is what <em>you</em> can see:
              without the <code>a.view</code> permission it shows only the agents your own teams
              reach.
            </p>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              {agentCatalog.map((agent) => {
                const has = allowedAgentIds.has(agent.id);
                return (
                  <button
                    key={agent.id}
                    type="button"
                    onClick={() => void toggleAllowedAgent(agent.id, has)}
                    title={`${PROVIDER_DISPLAY_NAMES[agent.provider]} — ${agent.model}`}
                    className={`rounded border px-2 py-1 text-[11px] transition-colors ${
                      has
                        ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-200"
                        : "border-border/50 bg-muted/30 text-muted-foreground hover:border-border"
                    }`}
                  >
                    {agent.name}
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Default agent
          </h4>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Applied when the user creates a thread and the project has no default. Takes precedence
            over the default model, because an agent carries its own provider and model.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <select
              value={user.defaultAgentId ?? ""}
              onChange={(event) => {
                const value = event.target.value;
                void setDefaultAgent(value ? (value as ManagedAgentId) : null);
              }}
              className="rounded border border-border/60 bg-background px-2 py-1 text-xs"
            >
              <option value="">None (use default model)</option>
              {defaultAgentChoices.map((agent) => (
                <option key={agent.id} value={agent.id}>
                  {agent.name} — {PROVIDER_DISPLAY_NAMES[agent.provider]} / {agent.model}
                </option>
              ))}
            </select>
          </div>
        </section>

        <div className="flex justify-end pt-2">
          <Button size="sm" onClick={props.onClose}>
            Done
          </Button>
        </div>
      </div>
    </ModalShell>
  );
}
